/**
 * IhtPage — UK Inheritance Tax Estimator
 *
 * The headline insight: from April 2027 SIPPs come INSIDE the estate for IHT.
 * Most of Simon's wealth sits in AJ Bell SIPPs, so this page frames the
 * "today vs post-April-2027" comparison as the central story.
 *
 * Inputs: spouse, children, property value, other assets, charity %, and a
 * toggle to preview the 2027 rules applied today.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Scale, AlertTriangle, Sparkles, RefreshCw, Info, TrendingUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import TaxWorkspaceTabs from '@/components/ui/TaxWorkspaceTabs'

interface ScenarioBreakdown {
  label: string
  estate_gbp: number
  nrb_gbp: number
  rnrb_gbp: number
  threshold_gbp: number
  taxable_gbp: number
  rate_pct: number
  bill_gbp: number
  effective_rate_pct: number
}

interface Recommendation {
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  title: string
  detail: string
  impact_gbp: number | null
}

interface IhtEstimate {
  headline: string
  tracked_total_gbp: number
  pension_excluded_gbp: number
  property_value_gbp: number
  other_assets_gbp: number
  current_estate_gbp: number
  estate_after_pension_exclusion_gbp: number
  nil_rate_band_total_gbp: number
  rnrb_gross_gbp: number
  rnrb_after_taper_gbp: number
  rnrb_total_gbp: number
  total_threshold_gbp: number
  taxable_estate_gbp: number
  iht_bill_gbp: number
  iht_bill_2027_gbp: number
  effective_rate_pct: number
  effective_rate_2027_pct: number
  delta_2027_gbp: number
  scenario_today: ScenarioBreakdown
  scenario_2027: ScenarioBreakdown
  recommendations: Recommendation[]
  assumptions: Record<string, any>
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtCompact = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

const PRIORITY_STYLES: Record<Recommendation['priority'], string> = {
  CRITICAL: 'border-rose/40 bg-rose/10 text-rose-light',
  HIGH:     'border-amber/40 bg-amber/10 text-amber',
  MEDIUM:   'border-blue/30 bg-blue/10 text-blue-light',
  LOW:      'border-border bg-surface-card text-text-secondary',
}

export default function IhtPage() {
  // Inputs
  const [spouse, setSpouse] = useState(true)
  const [children, setChildren] = useState(2)
  const [propertyValue, setPropertyValue] = useState(1_500_000)
  const [otherAssets, setOtherAssets] = useState(0)
  const [charityPct, setCharityPct] = useState(0)
  const [includePension2027, setIncludePension2027] = useState(false)

  const [data, setData] = useState<IhtEstimate | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchEstimate = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<IhtEstimate>('/api/iht/estimate', {
        params: {
          spouse,
          children,
          // Only pass an override if user entered something; 0 means "use seeded property"
          ...(propertyValue > 0 ? { property_value_override: propertyValue } : {}),
          other_assets: otherAssets,
          charity_pct: charityPct,
          include_pension_2027: includePension2027,
        },
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load IHT estimate')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEstimate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(fetchEstimate, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spouse, children, propertyValue, otherAssets, charityPct, includePension2027])

  // Visual delta between today and 2027 — the cliff-edge number
  const cliffEdge = useMemo(() => {
    if (!data) return 0
    return data.iht_bill_2027_gbp - data.iht_bill_gbp
  }, [data])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Scale size={20} className="text-blue-light" aria-hidden />
            Inheritance Tax Estimator
          </h1>
          <p className="text-sm text-text-secondary">
            UK IHT exposure today vs the post-April-2027 world where SIPPs come inside the estate.
          </p>
        </div>
        <button type="button" onClick={fetchEstimate} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <TaxWorkspaceTabs />

      {error && (
        <div className="card p-4 border-rose/40 bg-rose/10 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Net worth composition */}
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div className="stat-card">
            <p className="stat-label">Tracked Investments</p>
            <p className="stat-value">{fmt(data.tracked_total_gbp)}</p>
            <p className="text-xs text-text-muted">from _accounts/_holdings</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Pension (SIPP)</p>
            <p className="stat-value text-emerald-light">{fmt(data.pension_excluded_gbp)}</p>
            <p className="text-xs text-text-muted">outside estate until April 2027</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Property</p>
            <p className="stat-value text-text-secondary">{fmt(data.property_value_gbp)}</p>
            <p className="text-xs text-text-muted">primary residence</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Total Estate</p>
            <p className="stat-value">{fmt(data.current_estate_gbp)}</p>
            <p className="text-xs text-text-muted">tracked + property + other</p>
          </div>
        </div>
      )}

      {/* Headline bill */}
      {data && (
        <div className={clsx(
          'card p-6 border-2',
          data.iht_bill_gbp > 0 ? 'border-rose/40 bg-rose/5' : 'border-emerald/40 bg-emerald/5',
        )}>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <p className="panel-title">
                Estimated IHT bill — today
              </p>
              <h2 className="stat-hero text-text-primary mt-1">
                {fmt(data.iht_bill_gbp)}
              </h2>
              <p className="text-sm text-text-secondary mt-1">
                {(data.effective_rate_pct ?? 0).toFixed(1)}% of your £{(data.current_estate_gbp ?? 0).toLocaleString('en-GB')} estate
              </p>
              <p className="text-sm text-text-primary mt-3 leading-relaxed">{data.headline}</p>
            </div>
            {cliffEdge > 0 && (
              <div className="text-right shrink-0">
                <p className="text-xs uppercase tracking-wider text-amber">April 2027 cliff-edge</p>
                <p className="text-3xl font-bold text-amber mt-1">+{fmtCompact(cliffEdge)}</p>
                <p className="text-xs text-text-muted mt-1">when SIPPs enter the estate</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Assumptions
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberSlider
            label="Property value (primary residence)"
            value={propertyValue}
            onChange={setPropertyValue}
            min={0}
            max={5_000_000}
            step={25_000}
            format={fmt}
          />
          <NumberSlider
            label="Other assets (art, business, etc.)"
            value={otherAssets}
            onChange={setOtherAssets}
            min={0}
            max={2_000_000}
            step={10_000}
            format={fmt}
          />
          <NumberSlider
            label="Charity % of net estate"
            value={charityPct}
            onChange={setCharityPct}
            min={0}
            max={30}
            step={1}
            format={(v) => `${v}%`}
          />
          <NumberSlider
            label="Children / direct descendants"
            value={children}
            onChange={setChildren}
            min={0}
            max={8}
            step={1}
            format={(v) => `${v}`}
          />
          <label className="flex items-center gap-2 text-xs text-text-secondary self-end">
            <input
              type="checkbox"
              checked={spouse}
              onChange={(e) => setSpouse(e.target.checked)}
              className="rounded border-border bg-surface-card"
            />
            Married / civil partnership (unlocks transferable NRB + RNRB)
          </label>
          <label className="flex items-center gap-2 text-xs text-text-secondary self-end">
            <input
              type="checkbox"
              checked={includePension2027}
              onChange={(e) => setIncludePension2027(e.target.checked)}
              className="rounded border-border bg-surface-card"
            />
            Apply April 2027 rules to "today" (SIPP in estate)
          </label>
        </div>
      </div>

      {/* Side-by-side scenario comparison — the headline insight */}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <ScenarioCard
            scenario={data.scenario_today}
            colour="emerald"
            subtitle="Current rules — pensions excluded"
          />
          <ScenarioCard
            scenario={data.scenario_2027}
            colour="rose"
            subtitle="Post-April 2027 — pensions inside estate"
            highlight={cliffEdge > 0}
          />
        </div>
      )}

      {/* Threshold breakdown */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Info size={14} className="text-blue-light" aria-hidden />
            Threshold Breakdown
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-pro">
              <thead>
                <tr>
                  <th>Component</th>
                  <th className="!text-right">Today</th>
                  <th className="!text-right">April 2027+</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-text-primary">Estate value</td>
                  <td className="text-right font-mono">{fmt(data.scenario_today.estate_gbp)}</td>
                  <td className="text-right font-mono">{fmt(data.scenario_2027.estate_gbp)}</td>
                  <td className="text-xs text-text-muted">
                    + {fmt(data.pension_excluded_gbp)} SIPP from 2027
                  </td>
                </tr>
                <tr>
                  <td className="text-text-primary">Nil Rate Band (NRB)</td>
                  <td className="text-right font-mono text-emerald-light">
                    −{fmt(data.scenario_today.nrb_gbp)}
                  </td>
                  <td className="text-right font-mono text-emerald-light">
                    −{fmt(data.scenario_2027.nrb_gbp)}
                  </td>
                  <td className="text-xs text-text-muted">
                    £325k × {spouse ? 2 : 1}, frozen until 2028
                  </td>
                </tr>
                <tr>
                  <td className="text-text-primary">Residence NRB (gross)</td>
                  <td className="text-right font-mono text-text-muted">
                    {fmt(data.rnrb_gross_gbp)}
                  </td>
                  <td className="text-right font-mono text-text-muted">
                    {fmt(data.rnrb_gross_gbp)}
                  </td>
                  <td className="text-xs text-text-muted">
                    £175k × {spouse ? 2 : 1}, requires children + property
                  </td>
                </tr>
                <tr>
                  <td className="text-text-primary">Residence NRB (after taper)</td>
                  <td className="text-right font-mono text-emerald-light">
                    −{fmt(data.scenario_today.rnrb_gbp)}
                  </td>
                  <td className="text-right font-mono text-emerald-light">
                    −{fmt(data.scenario_2027.rnrb_gbp)}
                  </td>
                  <td className="text-xs text-text-muted">
                    Tapered £1 per £2 over £2M
                  </td>
                </tr>
                <tr className="bg-surface-card/50">
                  <td className="text-text-primary font-semibold">Total threshold</td>
                  <td className="text-right font-mono font-bold">{fmt(data.scenario_today.threshold_gbp)}</td>
                  <td className="text-right font-mono font-bold">{fmt(data.scenario_2027.threshold_gbp)}</td>
                  <td className="text-xs text-text-muted">NRB + RNRB combined</td>
                </tr>
                <tr>
                  <td className="text-text-primary">Taxable estate</td>
                  <td className="text-right font-mono text-amber">{fmt(data.scenario_today.taxable_gbp)}</td>
                  <td className="text-right font-mono text-amber">{fmt(data.scenario_2027.taxable_gbp)}</td>
                  <td className="text-xs text-text-muted">Estate − threshold</td>
                </tr>
                <tr className="bg-rose/5">
                  <td className="text-text-primary font-semibold">IHT bill @ {data.scenario_today.rate_pct}%</td>
                  <td className="text-right font-mono font-bold text-rose-light">
                    {fmt(data.scenario_today.bill_gbp)}
                  </td>
                  <td className="text-right font-mono font-bold text-rose-light">
                    {fmt(data.scenario_2027.bill_gbp)}
                  </td>
                  <td className="text-xs text-text-muted">
                    {charityPct >= 10 ? '36% (10%+ to charity)' : '40% standard rate'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data && data.recommendations.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-blue-light" aria-hidden />
            Mitigation Strategies
          </h2>
          <div className="space-y-3">
            {data.recommendations.map((r, i) => (
              <div
                key={i}
                className={clsx('border rounded-lg p-4', PRIORITY_STYLES[r.priority])}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider">
                        {r.priority}
                      </span>
                      <span className="text-sm font-semibold text-text-primary">{r.title}</span>
                    </div>
                    <p className="text-xs text-text-secondary leading-relaxed">{r.detail}</p>
                  </div>
                  {r.impact_gbp != null && r.impact_gbp > 0 && (
                    <div className="shrink-0 text-right">
                      <p className="text-[10px] uppercase text-text-muted">Potential saving</p>
                      <p className="text-sm font-bold text-emerald-light">
                        {fmtCompact(r.impact_gbp)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold flex items-center gap-2">
          <AlertTriangle size={12} className="text-amber" aria-hidden />
          Methodology &amp; UK IHT rules (2026/27)
        </summary>
        <div className="mt-3 space-y-2">
          <p>
            <span className="text-text-primary font-semibold">Nil Rate Band (NRB):</span>{' '}
            £325,000 per person, frozen until April 2028. Unused NRB transfers to a surviving spouse.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Residence Nil Rate Band (RNRB):</span>{' '}
            £175,000 per person, but only if the primary residence passes to direct descendants
            (children, grandchildren). Tapered by £1 for every £2 the estate exceeds £2M — so
            estates over £2.35M (single) / £2.7M (couple) lose it entirely.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Spousal exemption:</span>{' '}
            Anything left to a spouse is 100% IHT-exempt, and unused NRB/RNRB transfers on death.
            A married couple can shelter up to <strong>£1M</strong> (2 × NRB + 2 × RNRB).
          </p>
          <p>
            <span className="text-text-primary font-semibold">Rate:</span>{' '}
            40% above the threshold, or 36% if 10%+ of the net estate goes to charity.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Gifts:</span>{' '}
            £3,000 per person per year annual exemption (one-year carry-forward). Gifts over 7
            years before death are fully exempt; taper relief applies between years 3-7.
            "Gifts out of surplus income" are unlimited if regular and from income.
          </p>
          <p className="pt-2 border-t border-border text-amber">
            <span className="font-semibold">April 2027 change:</span>{' '}
            From April 2027 pensions (including SIPPs) form part of the estate for IHT purposes.
            This is the biggest single reform in a generation and — given how much of Simon's
            household wealth sits in AJ Bell SIPPs — is the dominant variable on this page.
            Strategies to consider before the deadline: crystallise the 25% tax-free lump sum and
            gift it, increase drawdown to fund regular gifts out of income, and earmark pension
            pots for charitable bequests while they're still pre-IHT.
          </p>
          <p className="text-text-muted pt-2">
            This estimate is indicative only. UK IHT planning is highly fact-specific — trusts,
            Business Relief, AIM shares, life cover in trust, and cross-border issues all matter.
            Consult a qualified tax advisor before acting.
          </p>
        </div>
      </details>
    </div>
  )
}

// ── Scenario card ────────────────────────────────────────────────────────────
function ScenarioCard({
  scenario,
  colour,
  subtitle,
  highlight,
}: {
  scenario: ScenarioBreakdown
  colour: 'emerald' | 'rose'
  subtitle: string
  highlight?: boolean
}) {
  const borderCls = colour === 'emerald' ? 'border-emerald/30' : 'border-rose/30'
  const bgCls = highlight ? 'bg-rose/5' : ''
  const textCls = colour === 'emerald' ? 'text-emerald-light' : 'text-rose-light'

  return (
    <div className={clsx('card p-5 border-2', borderCls, bgCls)}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
        {scenario.label}
      </p>
      <p className="text-xs text-text-secondary mb-3">{subtitle}</p>
      <div className="space-y-2 text-sm">
        <Row label="Estate" value={`£${(scenario.estate_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} />
        <Row label="NRB" value={`−£${(scenario.nrb_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} />
        <Row label="RNRB (after taper)" value={`−£${(scenario.rnrb_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} />
        <Row label="Threshold" value={`£${(scenario.threshold_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} strong />
        <Row label="Taxable" value={`£${(scenario.taxable_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} />
        <div className="pt-3 mt-3 border-t border-border">
          <p className="text-[10px] uppercase text-text-muted">IHT bill @ {scenario.rate_pct}%</p>
          <p className={clsx('text-3xl font-bold mt-1', textCls)}>
            £{(scenario.bill_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-text-muted mt-1">
            {(scenario.effective_rate_pct ?? 0).toFixed(1)}% effective rate on estate
          </p>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted text-xs">{label}</span>
      <span className={clsx('font-mono text-xs', strong ? 'font-bold text-text-primary' : 'text-text-secondary')}>
        {value}
      </span>
    </div>
  )
}

// ── Slider input component (matches PlanningPage styling) ────────────────────
function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}
